import { useEffect, useState } from "react";

import { Container, Grid2 as Grid, Grid2Props as GridProps } from "@mui/material";

import {
  ActionIcon,
  Autocomplete,
  Button,
  Center,
  CopyButton,
  Menu,
  Modal,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";

import {
  IconColumns,
  IconDots,
  IconDownload,
  IconExternalLink,
  IconFilter,
  IconLayoutGrid,
  IconReload,
  IconSelector,
  IconTrash,
} from "@tabler/icons-react";

import dayjs from "dayjs";
import { CSVLink } from "react-csv";

import DisplayAlert from "../../components/DisplayAlert";
import MegaLoader from "../../components/MegaLoader";
import TabularData from "../../components/TabularData";

import { CarsAndModels, Colors, SalesRequestStatuses, years } from "../../lib/Data";
import { Endpoints } from "../../lib/Endpoints";
import { PaginatedResponse } from "../../lib/Responses";
import {
  SaleRequestProps,
  SaleRequestStatusProps,
  ViewModesProps,
} from "../../lib/Types";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";

import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { Link } from "react-router-dom";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getColorFromRequestStatus,
  getPaginationTotal,
} from "../../lib/Methods";
import "./styles.scss";
import AnimateOnLoad from "../../components/AnimateOnLoad";
import DatagridTable from "../../components/DatagridTable";

const SalesRequests: React.FC = () => {
  const [pagination, setPagination] = useState({ page: 1, limit: 25 });

  const filterForm = useForm({
    initialValues: {
      status: "all",
      model: "",
      manufacturer: "",
      year: "",
    },
    validate: {
      status: (value) => (value && value.length > 2 ? null : "Select a status"),
    },
  });
  const {
    data: requests,
    isLoading: areRequestsLoading,
    reloadData: reloadRequests,
  } = usePerformRequest<PaginatedResponse<SaleRequestProps[]>>({
    method: "POST",
    route: Endpoints.GetSaleRequests(pagination.page, pagination.limit),
    body: {
      status: filterForm.values.status,
      model: filterForm.values.model,
      manufacturer: filterForm.values.manufacturer,
      year: filterForm.values.year,
    },
  });

  useEffect(() => {
    reloadRequests();
  }, [filterForm.values]);

  const [currentActionsMenu, setCurrentActionsMenu] = useState<string>("");

  const [viewMode, setViewMode] = useState<ViewModesProps>("table");

  const [isUpdating, setUpdating] = useState<boolean>(false);
  const UpdateSalesRequestStatus = async (
    requestID: string,
    status: SaleRequestStatusProps
  ) => {
    setUpdating(true);
    const r = await PerformRequest({
      method: "PATCH",
      data: {
        status,
      },
      route: Endpoints.GetUpdateSalesRequestStatus(requestID),
      callback: () => {
        setUpdating(false);
      },
    });
    setUpdating(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Request has been updated");
        reloadRequests();
      } else {
        DefaultErrorNotification((r?.data as any)[0]);
      }
    }
  };

  const [currentRequest, setCurrentRequest] = useState<SaleRequestProps>();
  const [
    isDeleteRequestModalOpen,
    { open: OpenDeleteRequestModal, close: CloseDeleteRequestModal },
  ] = useDisclosure(false);

  const DeleteSalesRequest = async () => {
    setUpdating(true);
    const r = await PerformRequest({
      method: "DELETE",
      route: Endpoints.GetDeleteSalesRequest(currentRequest?.id as string),
      callback: () => {
        setUpdating(false);
      },
    });
    setUpdating(false);
    if (r) {
      if (r.status === 200) {
        DefaultSuccessNotification("Request has been deleted");
        CloseDeleteRequestModal();
        setCurrentRequest(undefined);
        reloadRequests();
      } else {
        DefaultErrorNotification((r?.data as any)[0]);
      }
    }
  };
  const gridItemProps: GridProps = {
    size: {
      xl: 4,
      lg: 4,
      md: 6,
      sm: 12,
      xs: 12,
    },
  };
  const getInputProps = (id: string) => {
    return {
      label: id,
      w: 120,
      mr: 5,
      style: {
        textTransform: "capitalize",
      },
      spellCheck: false,
    } as any;
  };
  return (
    <AnimateOnLoad>
      <Container>
        <div className="sales-requests-container">
          <div className="flex-row align-center justify-between width-100">
            <div className="flex-col">
              <Text className="component-header">Sales Requests</Text>
              <div className="flex-row">
                <Select
                  allowDeselect={false}
                  {...filterForm.getInputProps("status")}
                  {...getInputProps("status")}
                  styles={{
                    option: {
                      textTransform: "capitalize",
                    },
                    input: {
                      textTransform: "capitalize",
                    },
                  }}
                  data={[
                    { label: "All", value: "all" },
                    ...SalesRequestStatuses.map((s) => {
                      return { label: s, value: s };
                    }),
                  ]}
                />
                <Autocomplete
                  clearable
                  rightSection={<IconSelector size={15} />}
                  searchable
                  {...filterForm.getInputProps("manufacturer")}
                  {...getInputProps("manufacturer")}
                  allowDeselect={false}
                  placeholder="E.g Honda"
                  data={CarsAndModels.map((c) => {
                    return {
                      label: c.brand === "" ? "All" : c.brand,
                      value: c.brand,
                    };
                  })}
                />
                <Autocomplete
                  clearable
                  rightSection={<IconSelector size={15} />}
                  searchable
                  data={years.map((year) => {
                    return {
                      label: year,
                      value: year,
                    };
                  })}
                  {...filterForm.getInputProps("year")}
                  {...getInputProps("year")}
                  placeholder="E.g 2001"
                />
                <Autocomplete
                  clearable
                  rightSection={<IconSelector size={15} />}
                  searchable
                  data={CarsAndModels.find(
                    (c) => c.brand === filterForm.values.manufacturer
                  )?.models?.map((model) => {
                    return {
                      label: model,
                      value: model,
                    };
                  })}
                  {...filterForm.getInputProps("model")}
                  {...getInputProps("model")}
                />
              </div>
            </div>
            <div className="flex-row align-center">
              <Button
                color={Colors.BluePrimary}
                style={{ borderRadius: "50px" }}
                loading={areRequestsLoading}
                onClick={reloadRequests}
                leftSection={<IconReload size={16} />}
              >
                Reload
              </Button>
              {requests && requests.data && requests.data.length > 0 && (
                <CSVLink
                  data={requests && requests.data ? requests.data : []}
                  headers={[
                    { label: "Name", key: "fullname" },
                    { label: "Email", key: "email" },
                    { label: "Phone", key: "phone" },
                    { label: "Manufacturer", key: "manufacturer" },
                    { label: "Model", key: "model" },
                    { label: "Year", key: "year" },
                    { label: "Status", key: "status" },
                    { label: "Vin", key: "vin" },
                  ]}
                >
                  <Button
                    ml={15}
                    color={Colors.BluePrimary}
                    variant="outline"
                    style={{ borderRadius: "50px" }}
                    disabled={
                      areRequestsLoading ||
                      !requests ||
                      !requests.data ||
                      requests.data.length === 0
                    }
                    leftSection={<IconDownload size={16} />}
                  >
                    Export
                  </Button>
                </CSVLink>
              )}
              <SegmentedControl
                mt={10}
                withItemsBorders={false}
                size="md"
                style={{ display: "none" }}
                value={viewMode}
                onChange={(value) => {
                  setViewMode(value as ViewModesProps);
                }}
                data={[
                  {
                    label: (
                      <Center style={{ gap: 10 }}>
                        <IconColumns size={20} />
                      </Center>
                    ),
                    value: "table",
                  },
                  {
                    label: (
                      <Center style={{ gap: 10 }}>
                        <IconLayoutGrid size={20} />
                      </Center>
                    ),
                    value: "grid",
                  },
                ]}
              />
            </div>
          </div>
          <br />
          <br />
          <>
            {requests && requests.data && requests.data.length > 0 ? (
              <DatagridTable
                isDataLoading={areRequestsLoading}
                rows={requests.data.map((r) => {
                  const {
                    id,
                    fullname: name,
                    manufacturer,
                    model,
                    year,
                    vin,
                    status,
                    createdAt,
                  } = r;
                  return {
                    id,
                    name,
                    manufacturer,
                    model,
                    year,
                    vin,
                    status,
                    createdAt,
                  };
                })}
                columns={[
                  {
                    flex: 1,
                    field: "name",
                    headerName: "Client Name",
                    width: 150,
                  },
                  {
                    flex: 1,
                    field: "manufacturer",
                    headerName: "Manufacturer",
                    width: 130,
                  },
                  {
                    flex: 1,
                    field: "model",
                    headerName: "Model",
                    width: 130,
                  },
                  {
                    field: "year",
                    headerName: "Year",
                    width: 90,
                  },
                  {
                    field: "vin",
                    headerName: "VIN",
                    width: 150,
                    renderCell: (params) => {
                      return (
                        <CopyButton value={params.row.vin}>
                          {({ copied, copy }) => (
                            <Tooltip label={copied ? "Copied to clipboard" : "Copy VIN"}>
                              <span
                                onClick={copy}
                                style={{
                                  cursor: "pointer",
                                  color: copied
                                    ? Colors.GreenPrimary
                                    : Colors.BluePrimary,
                                }}
                              >
                                {params.row.vin}
                              </span>
                            </Tooltip>
                          )}
                        </CopyButton>
                      );
                    },
                  },
                  {
                    field: "status",
                    headerName: "Status",
                    width: 110,
                    renderCell: (params) => {
                      return (
                        <span
                          style={{
                            color: getColorFromRequestStatus(params.row.status),
                          }}
                        >
                          {params.row.status}
                        </span>
                      );
                    },
                  },
                  {
                    flex: 1,
                    field: "createdAt",
                    headerName: "Date",
                    width: 150,
                    renderCell: (params) => {
                      return (
                        <span>
                          {dayjs(params.row.createdAt).format("ddd, DD MMM YYYY")}
                        </span>
                      );
                    },
                  },
                  {
                    field: "id",
                    headerName: "Actions",
                    renderCell: (params) => {
                      return (
                        <Menu
                          shadow="md"
                          opened={currentActionsMenu === params.row.id}
                          width={200}
                          trigger="hover"
                        >
                          <Menu.Target>
                            <ActionIcon
                              onClick={() => {
                                setCurrentActionsMenu(
                                  currentActionsMenu === params.row.id
                                    ? ""
                                    : params.row.id
                                );
                              }}
                              variant="subtle"
                            >
                              <IconDots size={15.5} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Label>Actions</Menu.Label>
                            <Menu.Item
                              leftSection={<IconFilter size={14} />}
                              disabled={isUpdating}
                            >
                              <Menu shadow="md" width={140}>
                                <Menu.Target>
                                  <Text fz={14}>Mark as</Text>
                                </Menu.Target>
                                <Menu.Dropdown>
                                  <div className="flex-col">
                                    {SalesRequestStatuses.map((status) => {
                                      return (
                                        <Button
                                          disabled={isUpdating}
                                          className="table-status-btn"
                                          tt="capitalize"
                                          variant="subtle"
                                          c={getColorFromRequestStatus(status)}
                                          mb={4}
                                          onClick={() => {
                                            UpdateSalesRequestStatus(
                                              params.row.id,
                                              status
                                            );
                                          }}
                                        >
                                          {status}
                                        </Button>
                                      );
                                    })}
                                  </div>
                                </Menu.Dropdown>
                              </Menu>
                            </Menu.Item>
                            <Menu.Item
                              leftSection={<IconTrash size={14} />}
                              disabled={isUpdating}
                              color={Colors.RedPrimary}
                              onClick={() => {
                                OpenDeleteRequestModal();
                                setCurrentActionsMenu("");
                                setCurrentRequest(params.row);
                              }}
                            >
                              Delete
                            </Menu.Item>
                            <Link to={`/dashboard/request/${params.row.id}`}>
                              <Menu.Item leftSection={<IconExternalLink size={14} />}>
                                View Details
                              </Menu.Item>
                            </Link>
                          </Menu.Dropdown>
                        </Menu>
                      );
                    },
                  },
                ]}
              />
            ) : (
              <>
                {areRequestsLoading ? (
                  <MegaLoader />
                ) : (
                  <DisplayAlert
                    title="No Requests Found"
                    children={<Text>No sales requests on the platform!</Text>}
                  />
                )}
              </>
            )}
          </>

          <div className="flex-row align-center justify-center width-100">
            <Pagination
              value={pagination.page}
              onChange={(value) => {
                setPagination({ ...pagination, page: value });
              }}
              my={40}
              disabled={requests?.data.length === 0 || areRequestsLoading}
              total={getPaginationTotal(requests?.totalRecords, pagination.limit)}
            />
          </div>
        </div>

        <Modal
          opened={isDeleteRequestModalOpen}
          onClose={() => {
            CloseDeleteRequestModal();
          }}
          centered
          title="Delete Request"
        >
          <Text c={Colors.BluePrimary} fz={22} fw={500}>
            {currentRequest?.year} {currentRequest?.manufacturer} {currentRequest?.model}
          </Text>
          <Text>Are you sure you want to delete this request</Text>
          <Text c={Colors.RedPrimary}>
            This action&nbsp;
            <b>CANNOT</b>&nbsp;be undone
          </Text>
          <div className="flex-row align-center">
            <Button
              loading={isUpdating}
              onClick={DeleteSalesRequest}
              mt={10}
              w={120}
              radius="50px"
              color={Colors.RedPrimary}
            >
              Delete
            </Button>
            &nbsp; &nbsp;
            <Button
              mt={10}
              w={120}
              radius="50px"
              color={Colors.GreenPrimary}
              variant="outline"
              onClick={CloseDeleteRequestModal}
            >
              Cancel
            </Button>
          </div>
        </Modal>
      </Container>
    </AnimateOnLoad>
  );
};
export default SalesRequests;
